import { CheckIcon, WarningCircleIcon } from '@phosphor-icons/react'
import { type FormEvent, useState } from 'react'
import { ApiError } from '../../api/client'
import { MIN_ACCOUNT_PASSWORD_LENGTH, changePassword } from '../../api/userland'
import { useAccountVault } from '../../hooks/useAccountVault'
import soft from '../../styles/soft.module.css'
import { figs } from '../ui/fig'
import styles from './settings.module.css'

// Settings → Security → Password, for an email account.
//
// Two steps, in this order. The server first checks the current password and
// sets the new one (POST /v1/me/password: other sessions are logged out, this
// one is renewed). Then the wallet's password slot is re-wrapped from the
// current password to the new one in the browser (useAccountVault
// setPasswordSlot), so the wallet opens with what the user now types. If that second
// step fails, the login has the new password and the wallet still opens with
// the old one, and the line says so.

export function ChangePassword() {
  const vault = useAccountVault()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setDone(false)
    if (!current) {
      setError('Enter the current password.')
      return
    }
    if (next.length < MIN_ACCOUNT_PASSWORD_LENGTH) {
      setError(`The new password needs at least ${MIN_ACCOUNT_PASSWORD_LENGTH} characters.`)
      return
    }
    if (next !== confirm) {
      setError('The new passwords do not match.')
      return
    }
    setBusy(true)
    const oldPassword = current
    const newPassword = next
    try {
      await changePassword(oldPassword, newPassword)
    } catch (err) {
      setError(changeFailMessage(err))
      setBusy(false)
      return
    }
    setCurrent('')
    setNext('')
    setConfirm('')
    try {
      // One operation: confirm the new password and, if the slot still takes
      // the old one, re-wrap it from it. (Two calls from this render's hook
      // object would see the state from before the click.)
      const slot = await vault.setPasswordSlot(newPassword, { password: oldPassword })
      if (slot === 'legacy')
        setError('Password changed. The wallet still opens with the old password.')
      else setDone(true)
    } catch {
      setError('Password changed. The wallet still opens with the old password.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles.group} aria-label="Password">
      <div className={styles.groupHead}>
        <h3 className={styles.caption}>Password</h3>
        <span className={styles.hint}>Changing it logs out your other sessions</span>
      </div>
      <form className={styles.passwordForm} onSubmit={(event) => void submit(event)}>
        <label className={`${soft.field} ${styles.tField}`}>
          <input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            placeholder="Current password"
            aria-label="Current password"
            disabled={busy}
          />
        </label>
        <label className={`${soft.field} ${styles.tField}`}>
          <input
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(event) => setNext(event.target.value)}
            placeholder="New password"
            aria-label="New password"
            disabled={busy}
          />
        </label>
        <label className={`${soft.field} ${styles.tField}`}>
          <input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            placeholder="Confirm password"
            aria-label="Confirm new password"
            disabled={busy}
          />
        </label>
        <button type="submit" className={`${soft.action} ${styles.tAdd}`} disabled={busy}>
          {figs(busy ? 'Saving…' : 'Save')}
        </button>
      </form>
      {error ? (
        <div className={styles.tError} role="alert">
          <WarningCircleIcon size={14} weight="bold" aria-hidden="true" />
          {figs(error)}
        </div>
      ) : done ? (
        <div className={styles.tNote}>
          <CheckIcon size={14} weight="bold" aria-hidden="true" />
          {figs('Password changed')}
        </div>
      ) : null}
    </section>
  )
}

function changeFailMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'wrong_password') return 'The current password is incorrect.'
    if (err.code === 'account_locked') return 'Too many attempts. Try again in 15 minutes.'
    if (err.code === 'password_too_weak') {
      return `The new password needs at least ${MIN_ACCOUNT_PASSWORD_LENGTH} characters.`
    }
    if (err.code === 'no_password') return 'This account logs in without a password.'
  }
  return 'Could not change the password. Try again.'
}
